/**
 * PostComposer Component - Mobile-first post creation with upload progress
 */

import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createPost, fetchLinkMetadata, LinkMetadata, Post } from '../../services/socialApi';
import { Link2, X, Loader2, Video, Image, MapPin } from 'lucide-react';
import './PostComposer.css';

const MAX_VIDEO_SIZE_MB = 100; // 100MB max video size

const API_BASE = '/api/social';

interface PostComposerProps {
  onClose?: () => void;
  onPostCreated?: () => void;
}

export const PostComposer: React.FC<PostComposerProps> = ({ onClose, onPostCreated }) => {
  const [content, setContent] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreview, setImagePreview] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Link post state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkPreview, setLinkPreview] = useState<LinkMetadata | null>(null);
  const [isFetchingLink, setIsFetchingLink] = useState(false);
  const [linkError, setLinkError] = useState('');

  // Video post state
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Location state
  const [location, setLocation] = useState<string | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length + selectedImages.length > 10) {
      setError('Maximum 10 images allowed');
      return;
    }

    const newPreviews: string[] = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target?.result as string);
        if (newPreviews.length === files.length) {
          setImagePreview(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    setSelectedImages(prev => [...prev, ...files]);
    setError('');
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  // Handle fetching link metadata
  const handleFetchLink = async () => {
    if (!linkUrl.trim()) {
      setLinkError('Please enter a URL');
      return;
    }

    setIsFetchingLink(true);
    setLinkError('');

    try {
      const metadata = await fetchLinkMetadata(linkUrl);
      setLinkPreview(metadata);
      setShowLinkModal(false);
      setLinkUrl('');
    } catch (err: any) {
      setLinkError(err.message || 'Failed to fetch link preview');
    } finally {
      setIsFetchingLink(false);
    }
  };

  // Remove link preview
  const removeLinkPreview = () => {
    setLinkPreview(null);
  };

  // Handle video selection
  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_VIDEO_SIZE_MB) {
      setError(`Video must be under ${MAX_VIDEO_SIZE_MB}MB. Your file is ${sizeMB.toFixed(1)}MB.`);
      return;
    }

    // Clear images if selecting video
    setSelectedImages([]);
    setImagePreview([]);

    setSelectedVideo(file);
    setVideoPreview(URL.createObjectURL(file));
    setError('');
  };

  // Remove video
  const removeVideo = () => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setSelectedVideo(null);
    setVideoPreview(null);
  };

  // Get user's location using Geolocation API
  const handleGetLocation = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    setError('');

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;

      // Use OpenStreetMap Nominatim for reverse geocoding (free, no API key needed)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=14`,
        {
          headers: {
            'Accept-Language': 'en',
            'User-Agent': 'SaveDelete/1.0'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Extract a user-friendly location name
        const address = data.address;
        let locationName = '';

        if (address.neighbourhood) {
          locationName = address.neighbourhood;
        } else if (address.suburb) {
          locationName = address.suburb;
        } else if (address.city_district) {
          locationName = address.city_district;
        }

        if (address.city || address.town || address.village) {
          const city = address.city || address.town || address.village;
          locationName = locationName ? `${locationName}, ${city}` : city;
        }

        if (address.country && !locationName.includes(address.country)) {
          locationName = locationName ? `${locationName}, ${address.country}` : address.country;
        }

        setLocation(locationName || data.display_name?.split(',').slice(0, 3).join(',') || 'Unknown location');
      } else {
        // Fallback: just show coordinates
        setLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
      }
    } catch (err: any) {
      if (err.code === 1) {
        setError('Location access denied. Please enable location permissions.');
      } else if (err.code === 2) {
        setError('Unable to determine your location. Please try again.');
      } else if (err.code === 3) {
        setError('Location request timed out. Please try again.');
      } else {
        setError('Failed to get location. Please try again.');
      }
    } finally {
      setIsGettingLocation(false);
    }
  };

  // Remove location
  const removeLocation = () => {
    setLocation(null);
  };

  // Upload with progress tracking using XMLHttpRequest
  const uploadWithProgress = (formData: FormData): Promise<Post> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const token = localStorage.getItem('authToken');

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch {
            reject(new Error('Invalid response from server'));
          }
        } else if (xhr.status === 401) {
          reject(new Error('Please log in to create a post'));
        } else if (xhr.status === 403) {
          reject(new Error('You don\'t have permission to post. Please complete your profile.'));
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.detail || errorData.error || 'Failed to create post'));
          } catch {
            reject(new Error('Failed to create post. Please try again.'));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error. Please check your connection.'));
      });

      xhr.open('POST', `${API_BASE}/posts/`);
      if (token) {
        xhr.setRequestHeader('Authorization', `Token ${token}`);
      }
      xhr.withCredentials = true;
      xhr.send(formData);
    });
  };

  const handleSubmit = async () => {
    // Check authentication first
    if (!isAuthenticated) {
      navigate('/login?redirect=/social/create');
      return;
    }

    if (!content.trim() && selectedImages.length === 0 && !linkPreview && !selectedVideo) {
      setError('Please add some content, media, or a link');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);
    setError('');

    try {
      if (selectedVideo) {
        // Upload video post
        const formData = new FormData();
        formData.append('content', content);
        formData.append('post_type', 'video');
        formData.append('visibility', 'public');
        formData.append('video_file', selectedVideo);
        if (location) formData.append('location', location);
        await uploadWithProgress(formData);
      } else if (selectedImages.length > 0) {
        const formData = new FormData();
        formData.append('content', content);
        formData.append('post_type', 'image');
        formData.append('visibility', 'public');
        selectedImages.forEach(file => {
          formData.append('media_files', file);
        });
        if (location) formData.append('location', location);
        await uploadWithProgress(formData);
      } else if (linkPreview) {
        // Create link post
        await createPost({
          content,
          post_type: 'link',
          visibility: 'public',
          link_url: linkPreview.url,
          location: location || undefined,
        });
      } else {
        await createPost({
          content,
          post_type: 'text',
          visibility: 'public',
          location: location || undefined,
        });
      }

      onPostCreated?.();
      onClose?.();
      navigate('/social');
    } catch (err: any) {
      setError(err.message || 'Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (content.trim() || selectedImages.length > 0) {
      if (window.confirm('Discard this post?')) {
        onClose?.();
        navigate(-1);
      }
    } else {
      onClose?.();
      navigate(-1);
    }
  };

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="post-composer">
        <header className="composer-header">
          <button className="close-btn" onClick={() => navigate(-1)}>
            ✕
          </button>
          <h2>New Post</h2>
          <div />
        </header>
        <div className="composer-body">
          <div className="login-prompt-container">
            <h3>Log in to post</h3>
            <p>You need to be logged in to create posts on SaveDelete.</p>
            <Link to="/login?redirect=/social/create" className="login-btn">
              Log In
            </Link>
            <p className="register-hint">
              Don't have an account? <Link to="/register">Sign up</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="post-composer">
      <header className="composer-header">
        <button className="close-btn" onClick={handleClose}>
          ✕
        </button>
        <h2>New Post</h2>
        <button
          className="post-btn"
          onClick={handleSubmit}
          disabled={isSubmitting || (!content.trim() && selectedImages.length === 0 && !linkPreview && !selectedVideo)}
        >
          {isSubmitting ? 'Posting...' : 'Post'}
        </button>
      </header>

      <div className="composer-body">
        {/* Show current user info */}
        <div className="composer-user-info">
          <div className="user-avatar-small">
            {user?.first_name?.[0] || user?.username?.[0] || '?'}
          </div>
          <span className="user-name">@{user?.username || 'user'}</span>
        </div>

        <div className="composer-input-area">
          <textarea
            className="content-input"
            placeholder="What's on your mind?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={10000}
            autoFocus
          />
        </div>

        {error && <div className="composer-error">{error}</div>}

        {/* Upload Progress Bar */}
        {isSubmitting && (selectedImages.length > 0 || selectedVideo) && (
          <div className="upload-progress">
            <div className="progress-bar-container">
              <div
                className="progress-bar"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <span className="progress-text">
              {uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing...'}
            </span>
          </div>
        )}

        {imagePreview.length > 0 && (
          <div className="image-preview-grid">
            {imagePreview.map((preview, index) => (
              <div key={index} className="preview-item">
                <img src={preview} alt={`Preview ${index + 1}`} />
                <button
                  className="remove-image"
                  onClick={() => removeImage(index)}
                  disabled={isSubmitting}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Link Preview */}
        {linkPreview && (
          <div className="link-preview-card">
            <button
              className="remove-link"
              onClick={removeLinkPreview}
              disabled={isSubmitting}
            >
              <X size={16} />
            </button>
            {linkPreview.image && (
              <img src={linkPreview.image} alt="" className="link-preview-image" />
            )}
            <div className="link-preview-info">
              <div className="link-preview-domain">
                {linkPreview.favicon && (
                  <img src={linkPreview.favicon} alt="" className="link-favicon" />
                )}
                <span>{linkPreview.domain}</span>
              </div>
              <h4 className="link-preview-title">{linkPreview.title}</h4>
              {linkPreview.description && (
                <p className="link-preview-description">{linkPreview.description}</p>
              )}
            </div>
          </div>
        )}

        {/* Video Preview */}
        {videoPreview && (
          <div className="video-preview-card">
            <button
              className="remove-video"
              onClick={removeVideo}
              disabled={isSubmitting}
            >
              <X size={16} />
            </button>
            <video
              src={videoPreview}
              controls
              className="video-preview-player"
            />
            <div className="video-info">
              <Video size={16} />
              <span>{selectedVideo?.name}</span>
              <span className="video-size">
                ({(selectedVideo?.size ? selectedVideo.size / (1024 * 1024) : 0).toFixed(1)}MB)
              </span>
            </div>
          </div>
        )}
      </div>

      <footer className="composer-footer">
        <div className="composer-actions">
          <button
            className={`action-btn ${selectedImages.length > 0 ? 'active' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting || !!selectedVideo}
          >
            <Image size={18} /> Photo
          </button>
          <button
            className={`action-btn ${selectedVideo ? 'active' : ''}`}
            onClick={() => videoInputRef.current?.click()}
            disabled={isSubmitting || selectedImages.length > 0}
          >
            <Video size={18} /> Video
          </button>
          <button
            className={`action-btn ${linkPreview ? 'active' : ''}`}
            onClick={() => setShowLinkModal(true)}
            disabled={isSubmitting || !!linkPreview}
          >
            <Link2 size={18} /> Link
          </button>
          <button
            className={`action-btn ${location ? 'active' : ''}`}
            onClick={handleGetLocation}
            disabled={isSubmitting || isGettingLocation || !!location}
          >
            {isGettingLocation ? (
              <>
                <Loader2 size={18} className="spin" /> Getting...
              </>
            ) : (
              <>
                <MapPin size={18} /> Location
              </>
            )}
          </button>
        </div>
        <span className="char-count">{content.length}/10000</span>
      </footer>

      {/* Location Tag */}
      {location && (
        <div className="location-tag">
          <MapPin size={14} />
          <span>{location}</span>
          <button
            className="remove-location"
            onClick={removeLocation}
            disabled={isSubmitting}
          >
            <X size={14} />
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,image/gif"
        multiple
        onChange={handleImageSelect}
        style={{ display: 'none' }}
      />

      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoSelect}
        style={{ display: 'none' }}
      />

      {/* Link Modal */}
      {showLinkModal && (
        <div className="link-modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="link-modal" onClick={(e) => e.stopPropagation()}>
            <div className="link-modal-header">
              <h3>Add Link</h3>
              <button
                className="link-modal-close"
                onClick={() => { setShowLinkModal(false); setLinkUrl(''); setLinkError(''); }}
              >
                <X size={20} />
              </button>
            </div>
            <div className="link-modal-body">
              <input
                type="url"
                placeholder="Enter URL (e.g., https://example.com)"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleFetchLink()}
                className="link-url-input"
                autoFocus
              />
              {linkError && <p className="link-error">{linkError}</p>}
            </div>
            <div className="link-modal-footer">
              <button
                className="link-cancel-btn"
                onClick={() => { setShowLinkModal(false); setLinkUrl(''); setLinkError(''); }}
              >
                Cancel
              </button>
              <button
                className="link-add-btn"
                onClick={handleFetchLink}
                disabled={isFetchingLink || !linkUrl.trim()}
              >
                {isFetchingLink ? (
                  <>
                    <Loader2 size={16} className="spin" /> Fetching...
                  </>
                ) : (
                  'Add Link'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostComposer;
